import { useEffect, useMemo, useState } from 'react';
import { formatInOrgTz } from '../lib/datetime';
import TopBar from '../components/TopBar';
import CampaignWizardModal from '../components/CampaignWizardModal';
import DeadLeadConversation from '../components/DeadLeadConversation';
import { deadLeadsService } from '../lib/services';
import { useApi, useMutation } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { useCrm } from '../lib/crm';

// Ordered states for the funnel — mirrors CampaignTarget.State enum
// progression. We always render every bucket (even when zero) so the
// widget feels stable across reloads.
const FUNNEL_STATES = [
  ['QUEUED',      'Queued',      'var(--text-tertiary)'],
  ['CONTACTED',   'Contacted',   '#5fa8ff'],
  ['REPLIED',     'Replied',     'var(--success)'],
  ['CONVERTED',   'Converted',   '#22c55e'],
  ['OPT_OUT',     'Opt-out',     'var(--warning)'],
  ['DNC_BLOCKED', 'DNC blocked', 'var(--danger)'],
  ['FAILED',      'Failed',      'var(--text-muted)'],
];

const SEGMENT_LABELS = {
  ORPHANED:          'Orphaned',
  AGED:              'Aged',
  SERVICE_TO_SALES:  'Service → Sales',
  LONG_TERM_NURTURE: 'Long-Term Nurture',
};

const STATUS_COLORS = {
  DRAFT:     'var(--text-tertiary)',
  REVIEW:    'var(--warning)',
  RUNNING:   'var(--success)',
  PAUSED:    'var(--warning)',
  DONE:      'var(--text-muted)',
  CANCELLED: 'var(--danger)',
};

function fmtNumber(n) { return n == null ? 0 : Number(n).toLocaleString(); }
function fmtDate(iso) {
  return formatInOrgTz(iso, { month: 'short', day: 'numeric' });
}

export default function DeadLeads() {
  const { toast } = useToast();
  const { getCustomer } = useCrm();

  // Resolve a customer UUID → display name / phone using the loaded CRM list.
  const custName = (id) => getCustomer(id)?.name || `#${String(id || '').slice(0, 8)}`;
  const custPhone = (id) => {
    const p = getCustomer(id)?.phone;
    return p && p !== '—' ? p : null;
  };

  // ---- data sources (15s polling like the lead board) ---------------------
  const { data: campaigns, refetch: refetchCampaigns } = useApi(
    () => deadLeadsService.listCampaigns(),
    { fallback: [], deps: [] }
  );
  const { data: funnel, refetch: refetchFunnel } = useApi(
    () => deadLeadsService.overallFunnel(),
    { fallback: { byState: {}, bySegment: {}, activeCampaigns: 0 }, deps: [] }
  );
  const { data: pool, refetch: refetchPool } = useApi(
    () => deadLeadsService.listPool({ size: 100 }),
    { fallback: [], deps: [] }
  );
  const { data: personas } = useApi(
    () => deadLeadsService.listPersonas(),
    { fallback: [], deps: [] }
  );
  const personaLabel = useMemo(() => {
    const m = {};
    (personas || []).forEach(p => { m[p.id] = p.label || p.personaKey; });
    return (id) => (id ? (m[id] || '—') : '—');
  }, [personas]);

  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState !== 'hidden') {
        refetchCampaigns();
        refetchFunnel();
        refetchPool();
      }
    }, 15_000);
    return () => clearInterval(t);
  }, [refetchCampaigns, refetchFunnel, refetchPool]);

  // ---- selected campaign drill-down ---------------------------------------
  const [selectedId, setSelectedId] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [convoTargetId, setConvoTargetId] = useState(null);
  const { data: targets, refetch: refetchTargets } = useApi(
    () => selectedId
      ? deadLeadsService.listTargets({ campaignId: selectedId, limit: 200 })
      : Promise.resolve([]),
    { fallback: [], deps: [selectedId] }
  );

  // Close the campaign / batch details popup on Escape.
  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e) => { if (e.key === 'Escape') setSelectedId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId]);

  // ---- mutations -----------------------------------------------------------
  const launch = useMutation(deadLeadsService.launchCampaign);
  const pause  = useMutation(deadLeadsService.pauseCampaign);
  const resume = useMutation(deadLeadsService.resumeCampaign);
  const cancel = useMutation(deadLeadsService.cancelCampaign);
  const del    = useMutation(deadLeadsService.deleteCampaign);
  const promote = useMutation(deadLeadsService.promote);
  const moveToLead     = useMutation(deadLeadsService.moveToLead);
  const moveToCustomer = useMutation(deadLeadsService.moveToCustomer);
  const buildBatches   = useMutation(deadLeadsService.buildBatches);
  const editPoolStatus = useMutation(deadLeadsService.updatePoolStatus);

  const onEditPoolStatus = async (row, status) => {
    if (!status || status === row.status) return;
    try {
      await editPoolStatus.mutate(row.id, { status });
      toast(`Pool status → ${status}`, { variant: 'success' });
      refreshAll();
    } catch (err) {
      toast(`Status change failed: ${err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  const onBuildBatches = async () => {
    try {
      const created = await buildBatches.mutate({});
      const n = Array.isArray(created) ? created.length : 0;
      toast(n > 0 ? `Built ${n} batch${n === 1 ? '' : 'es'} — awaiting review` : 'No lost leads available to batch (all are already in a campaign or moved out)', { variant: n > 0 ? 'success' : 'info' });
      refreshAll();
    } catch (err) {
      toast(`Build batches failed: ${err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  const refreshAll = () => {
    refetchCampaigns();
    refetchFunnel();
    refetchPool();
    if (selectedId) refetchTargets();
  };

  const onMovePool = async (mutation, label, row) => {
    try {
      await mutation.mutate(row.id);
      toast(`${label} (pool row ${String(row.id).slice(0, 8)})`, { variant: 'success' });
      refreshAll();
    } catch (err) {
      toast(`${label} failed: ${err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  const onAction = async (action, label, campaign) => {
    if (label === 'Deleted' &&
        !window.confirm(`Delete batch "${campaign.name}"? Its targets are removed and its leads return to the pool.`)) {
      return;
    }
    try {
      await action.mutate(campaign.id);
      toast(`${label} "${campaign.name}"`, { variant: 'success' });
      refreshAll();
    } catch (err) {
      toast(`${label} failed: ${err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  // ---- group campaigns by status for the columns --------------------------
  const grouped = useMemo(() => {
    const out = { REVIEW: [], RUNNING: [], PAUSED: [], DONE: [], DRAFT: [], CANCELLED: [] };
    (campaigns || []).forEach(c => {
      const bucket = out[c.status] || out.DRAFT;
      bucket.push(c);
    });
    return out;
  }, [campaigns]);

  const byState  = funnel?.byState  || {};
  const bySegment = funnel?.bySegment || {};
  const selectedCampaign = (campaigns || []).find(c => c.id === selectedId) || null;

  return (
    <>
      <TopBar
        crumbs={['Lost Leads']}
        right={
          <>
            <button className="btn small" onClick={refreshAll}>↻ Refresh</button>
            <button className="btn small" disabled={buildBatches.mutating} onClick={onBuildBatches}>
              Build batches
            </button>
            <button className="btn small primary" onClick={() => setWizardOpen(true)}>
              + New campaign
            </button>
          </>
        }
      />
      <div className="app-content">

        {/* ===== Funnel widget ===== */}
        <div className="card animate-in" style={{ marginBottom: 16 }}>
          <div className="flex-between" style={{ marginBottom: 10 }}>
            <div className="card-label">CAMPAIGN FUNNEL · ALL ACTIVE CAMPAIGNS</div>
            <span className="mono text-xs text-muted">
              {fmtNumber(funnel?.activeCampaigns)} campaigns
            </span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${FUNNEL_STATES.length}, 1fr)`,
            gap: 10,
          }}>
            {FUNNEL_STATES.map(([key, label, color]) => (
              <div key={key} style={{ textAlign: 'center' }}>
                <div className="mono text-xs text-muted" style={{ letterSpacing: 0.4 }}>
                  {label.toUpperCase()}
                </div>
                <div className="metric-value"
                     style={{ fontSize: 22, color, marginTop: 2 }}>
                  {fmtNumber(byState[key] || 0)}
                </div>
              </div>
            ))}
          </div>
          {Object.keys(bySegment).length > 0 && (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
              <div className="card-label" style={{ marginBottom: 8 }}>BY SEGMENT</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                {Object.entries(bySegment).map(([seg, counts]) => {
                  const total = Object.values(counts).reduce((a, b) => a + Number(b || 0), 0);
                  return (
                    <div key={seg} className="stat-tile">
                      <div className="mono text-xs text-muted">{SEGMENT_LABELS[seg] || seg}</div>
                      <div style={{ fontSize: 18, fontWeight: 600 }}>{fmtNumber(total)}</div>
                      <div className="mono text-xs text-muted">
                        {fmtNumber(counts.CONTACTED || 0)} sent · {fmtNumber(counts.REPLIED || 0)} replied
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ===== Dead-lead pool / inbox ===== */}
        <div className="card animate-in" style={{ marginBottom: 16, padding: 0 }}>
          <div className="flex-between" style={{ padding: '14px 14px 6px' }}>
            <div className="card-label">DEAD-LEAD POOL · MANUAL + AUTO</div>
            <span className="mono text-xs text-muted">{fmtNumber((pool || []).length)} in pool</span>
          </div>
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                {['Pool', 'Customer', 'Status', 'Persona', 'Score', 'Source', 'Reason', 'Added', 'Actions'].map(h => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {(pool || []).length === 0 && (
                <tr><td colSpan={9} className="text-muted" style={{ padding: 14, textAlign: 'center' }}>
                  Pool is empty — mark a lead or customer dead, or wait for the nightly scan.
                </td></tr>
              )}
              {(pool || []).map(d => {
                // Only hide the move-back controls once the lead has actually
                // left the pool (REVIVED → became a lead, ARCHIVED → moved to
                // customer). Everything else, including DISQUALIFIED, can still
                // be moved back manually by an admin.
                const movedOut = ['REVIVED', 'ARCHIVED'].includes(d.status);
                return (
                  <tr key={d.id}>
                    <td className="mono text-xs">{String(d.id).slice(0, 8)}</td>
                    <td>
                      <a href={`/customers/${d.customerId}`} style={{ fontWeight: 500 }}>{custName(d.customerId)}</a>
                      {custPhone(d.customerId) && (
                        <div className="mono text-xs text-muted">{custPhone(d.customerId)}</div>
                      )}
                    </td>
                    <td>
                      <select
                        value={d.status}
                        disabled={editPoolStatus.mutating}
                        onChange={(e) => onEditPoolStatus(d, e.target.value)}
                        title="Change pool status (e.g. BATCHED → QUALIFIED to re-batch)"
                        style={{
                          color: poolStatusColor(d.status), fontWeight: 600,
                          background: 'var(--bg-glass)', border: '1px solid var(--border-color)',
                          borderRadius: 4, fontSize: 11, padding: '2px 4px', cursor: 'pointer',
                        }}>
                        {POOL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="text-xs">{personaLabel(d.personaId)}</td>
                    <td className="mono text-xs">{d.qualification?.score ?? '—'}</td>
                    <td className="mono text-xs text-muted">{d.source || '—'}</td>
                    <td className="mono text-xs text-muted">{d.deadReason || '—'}</td>
                    <td className="mono text-xs">{fmtDate(d.createdAt)}</td>
                    <td>
                      {!movedOut && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn small primary"
                                  disabled={moveToLead.mutating}
                                  title="Move back to Leads (reactivate as a hot lead)"
                                  onClick={() => onMovePool(moveToLead, 'Moved to Leads', d)}>
                            → Leads
                          </button>
                          <button className="btn small"
                                  disabled={moveToCustomer.mutating}
                                  title="Not a lost lead — move back to Customers"
                                  onClick={() => onMovePool(moveToCustomer, 'Moved to Customers', d)}>
                            → Customers
                          </button>
                        </div>
                      )}
                      {movedOut && <span className="mono text-xs text-muted">{d.status === 'REVIVED' ? '→ lead' : '→ customer'}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

        {/* ===== Campaign columns ===== */}
        <div style={{ display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                      gap: 14 }}>
          <CampaignColumn title="Draft / awaiting review" status="REVIEW"
                          campaigns={[...grouped.DRAFT, ...grouped.REVIEW]} selectedId={selectedId}
                          onSelect={setSelectedId}
                          actions={[
                            ['Launch', launch, 'Launched', 'primary'],
                            ['Cancel', cancel, 'Cancelled', ''],
                            ['Delete', del, 'Deleted', 'danger'],
                          ]}
                          onAction={onAction} />
          <CampaignColumn title="Running" status="RUNNING"
                          campaigns={grouped.RUNNING} selectedId={selectedId}
                          onSelect={setSelectedId}
                          actions={[
                            ['Pause',  pause,  'Paused', ''],
                            ['Cancel', cancel, 'Cancelled', ''],
                          ]}
                          onAction={onAction} />
          <CampaignColumn title="Paused" status="PAUSED"
                          campaigns={grouped.PAUSED} selectedId={selectedId}
                          onSelect={setSelectedId}
                          actions={[
                            ['Resume', resume, 'Resumed', 'primary'],
                            ['Cancel', cancel, 'Cancelled', ''],
                            ['Delete', del, 'Deleted', 'danger'],
                          ]}
                          onAction={onAction} />
          <CampaignColumn title="Done / cancelled"
                          status="DONE"
                          campaigns={[...grouped.DONE, ...grouped.CANCELLED]}
                          selectedId={selectedId}
                          onSelect={setSelectedId}
                          actions={[
                            ['Delete', del, 'Deleted', 'danger'],
                          ]}
                          onAction={onAction} />
        </div>

        <CampaignWizardModal
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onCreated={(c) => {
            toast(`Campaign "${c?.name || 'created'}" saved as DRAFT`, { variant: 'success' });
            refreshAll();
          }}
        />

        {/* ===== Campaign / batch details popup ===== */}
        {selectedId && selectedCampaign && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="batch-modal-title"
            className="modal-backdrop"
            onClick={() => setSelectedId(null)}
          >
            <div
              className="modal"
              style={{ width: 'min(960px, 95vw)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div>
                  <div id="batch-modal-title" style={{ fontSize: 16, fontWeight: 600 }}>
                    {selectedCampaign.name}
                  </div>
                  <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
                    Campaign &amp; batch details · {(targets || []).length} target{(targets || []).length === 1 ? '' : 's'}
                  </div>
                </div>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setSelectedId(null)}
                  aria-label="Close"
                >✕</button>
              </div>

              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Campaign summary */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22 }}>
                  <Meta label="Status" value={selectedCampaign.status}
                        color={STATUS_COLORS[selectedCampaign.status]} />
                  <Meta label="Segment"
                        value={SEGMENT_LABELS[selectedCampaign.segment] || selectedCampaign.segment} />
                  <Meta label="Cohort size" value={fmtNumber(selectedCampaign.cohortSize)} />
                  <Meta label="Created" value={fmtDate(selectedCampaign.createdAt)} />
                  {selectedCampaign.startedAt && (
                    <Meta label="Launched" value={fmtDate(selectedCampaign.startedAt)} />
                  )}
                  {selectedCampaign.autoGenerated && (
                    <Meta label="Origin" value="Auto-generated" />
                  )}
                </div>

                {/* Targets table */}
                <div>
                  <div className="card-label" style={{ marginBottom: 8 }}>TARGETS</div>
                  <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        {['Customer', 'State', 'Step', 'Channel', 'Last touched', 'Outcome', ''].map(h => <th key={h}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {(targets || []).length === 0 && (
                        <tr><td colSpan={7} className="text-muted" style={{ padding: 14, textAlign: 'center' }}>
                          No targets yet — eligibility scan runs at 02:00 AEST nightly.
                        </td></tr>
                      )}
                      {(targets || []).map(t => (
                        <tr key={t.id}>
                          <td>
                            <a href={`/customers/${t.customerId}`} style={{ fontWeight: 500 }}>{custName(t.customerId)}</a>
                            <div className="mono text-xs text-muted">
                              {custPhone(t.customerId) || `#${t.id}`}
                            </div>
                          </td>
                          <td>
                            <span style={{ color: stateColor(t.state) }}>{t.state}</span>
                          </td>
                          <td className="mono text-xs">{t.currentStep ?? 0}</td>
                          <td>{t.lastChannel || '—'}</td>
                          <td className="mono text-xs">{fmtDate(t.lastTouchedAt)}</td>
                          <td className="mono text-xs text-muted">{t.lastOutcome || '—'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button className="btn small" onClick={() => setConvoTargetId(t.id)}>View</button>
                              {(t.state === 'REPLIED' || t.state === 'CONTACTED') && !t.revivalLeadId && (
                                <button className="btn small primary"
                                        disabled={promote.mutating}
                                        onClick={async () => {
                                          try {
                                            await promote.mutate(t.id, {});
                                            toast(`Promoted target #${t.id} to lead`, { variant: 'success' });
                                            refreshAll();
                                          } catch (e) {
                                            toast(`Promote failed: ${e?.message}`, { variant: 'danger' });
                                          }
                                        }}>
                                  Promote
                                </button>
                              )}
                              {t.revivalLeadId && (
                                <span className="mono text-xs text-muted">→ lead</span>
                              )}
                              {t.deadLeadId && !t.revivalLeadId && (
                                <>
                                  <button className="btn small"
                                          disabled={moveToLead.mutating}
                                          onClick={() => onMovePool(moveToLead, 'Moved to Leads', { id: t.deadLeadId })}>
                                    → Leads
                                  </button>
                                  <button className="btn small"
                                          disabled={moveToCustomer.mutating}
                                          onClick={() => onMovePool(moveToCustomer, 'Moved to Customers', { id: t.deadLeadId })}>
                                    → Customers
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <DeadLeadConversation
          open={!!convoTargetId}
          targetId={convoTargetId}
          onClose={() => setConvoTargetId(null)}
        />
      </div>
    </>
  );
}

function stateColor(state) {
  const row = FUNNEL_STATES.find(r => r[0] === state);
  return row ? row[2] : 'var(--text-secondary)';
}

const POOL_STATUSES = [
  'NEW', 'QUALIFYING', 'QUALIFIED', 'DISQUALIFIED',
  'BATCHED', 'IN_CAMPAIGN', 'REVIVED', 'ARCHIVED',
];
const POOL_STATUS_COLORS = {
  NEW:          'var(--text-tertiary)',
  QUALIFYING:   '#5fa8ff',
  QUALIFIED:    'var(--success)',
  DISQUALIFIED: 'var(--text-muted)',
  BATCHED:      '#5fa8ff',
  IN_CAMPAIGN:  'var(--accent)',
  REVIVED:      '#22c55e',
  ARCHIVED:     'var(--text-muted)',
};
function poolStatusColor(status) {
  return POOL_STATUS_COLORS[status] || 'var(--text-secondary)';
}

/* Small label/value pair used in the campaign details popup. */
function Meta({ label, value, color }) {
  return (
    <div>
      <div className="card-label" style={{ marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: color || 'var(--text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

function CampaignColumn({ title, status, campaigns, selectedId, onSelect, actions, onAction }) {
  return (
    <div className="card animate-in" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="flex-between" style={{ marginBottom: 10 }}>
        <div className="card-label">{title.toUpperCase()}</div>
        <span className="badge" style={{ background: STATUS_COLORS[status] + '22',
                                          color: STATUS_COLORS[status] }}>
          {fmtNumber(campaigns.length)}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {campaigns.length === 0 && (
          <div className="text-muted text-xs" style={{ padding: '14px 4px' }}>
            No campaigns
          </div>
        )}
        {campaigns.map(c => (
          <div key={c.id}
               onClick={() => onSelect(selectedId === c.id ? null : c.id)}
               className={`campaign-card ${selectedId === c.id ? 'selected' : ''}`}>
            <div className="flex-between" style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</div>
              {c.autoGenerated && (
                <span className="mono text-xs text-muted" title="Created by EligibilityJob">AUTO</span>
              )}
            </div>
            <div className="mono text-xs text-muted">
              {SEGMENT_LABELS[c.segment] || c.segment} · cohort {fmtNumber(c.cohortSize)}
            </div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              created {fmtDate(c.createdAt)}{c.startedAt ? ` · launched ${fmtDate(c.startedAt)}` : ''}
            </div>
            {actions.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}
                   onClick={e => e.stopPropagation()}>
                {actions.map(([label, mutation, pastTense, variant]) => (
                  <button key={label}
                          className={`btn small ${variant || ''}`}
                          disabled={mutation.mutating}
                          onClick={() => onAction(mutation, pastTense, c)}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
